import React, { useEffect, useMemo, useState } from 'react';
import { Alert, CircularProgress, Rating, Snackbar } from '@mui/material';
import {
  Add,
  ArrowBack,
  Check,
  CheckCircle,
  EditOutlined,
  Equalizer,
  Link as LinkIcon,
  LocalFireDepartment,
  MenuBookOutlined,
  OpenInNew,
  Remove,
  Restaurant,
  Schedule,
  Star,
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AddToMenuPopup from '../AddToMenuPopup';
import ExperimentalPopupProvider from '../ExperimentalPopups/ExperimentalPopupProvider';
import './RecipeDetailV2.css';

const previewRecipe = {
  _id: 'preview-recipe',
  name: 'Bowl mediterráneo de pollo y verduras',
  mainImage: 'https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1400&q=86',
  momentoDelDia: ['Comida', 'Cena'],
  tipoPlato: ['Principal'],
  difficulty: 'Baja',
  prepTime: { hours: 0, minutes: 30 },
  servings: 2,
  rating: 4.8,
  ratingCount: 38,
  userRating: 5,
  userUsageCount: 7,
  revisado: true,
  tipoDeCocina: [{ name: 'Mediterránea' }],
  restricciones: [{ name: 'Sin lactosa' }],
  totalMacronutrients: { kilocalories: 610, protein: 44, carbs: 68, fat: 19 },
  externalUrl: 'https://example.com',
  steps: [
    'Cuece el arroz integral hasta que quede tierno y déjalo reposar cinco minutos.',
    'Saltea el pollo con pimentón, ajo y un toque de limón hasta que esté dorado.',
    'Añade las verduras y cocínalas a fuego vivo para mantener su textura.',
    'Monta el bowl con el arroz como base, reparte el pollo y termina con las verduras y las hierbas frescas.',
  ],
};

const previewIngredients = {
  chicken: { name: 'Pechuga de pollo', grams: 320, standardUnit: 'gramos' },
  rice: { name: 'Arroz integral', grams: 160, standardUnit: 'gramos' },
  pepper: { name: 'Pimiento rojo', grams: 140, standardUnit: 'gramos' },
  zucchini: { name: 'Calabacín', grams: 180, standardUnit: 'gramos' },
  lemon: { name: 'Limón', grams: 90, standardUnit: 'uds', gramsPerUnit: 90 },
  oil: { name: 'Aceite de oliva', grams: 20, standardUnit: 'cucharadas', gramsPerUnit: 15 },
};

const number = (value) => Number(value) || 0;
const formatAmount = (value) => {
  if (!Number.isFinite(value)) return '0';
  const rounded = value >= 10 ? Math.round(value) : value >= 1 ? Math.round(value * 10) / 10 : Math.round(value * 100) / 100;
  return String(rounded).replace('.', ',');
};
const getImage = (path) => {
  if (!path) return '';
  if (/^(https?:|data:)/.test(path)) return path;
  const api = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';
  return `${api}/${String(path).replace(/\\/g, '/').replace(/^\//, '')}`;
};
const labelFor = (grams, ingredient = {}) => {
  const unit = ingredient.standardUnit || 'gramos';
  const factor = number(ingredient.gramsPerUnit);
  if (unit === 'gramos' || !factor) return `${formatAmount(grams)} g`;
  return `${formatAmount(grams / factor)} ${unit}`;
};
const nameFor = (value) => typeof value === 'string' ? value : value?.name || value?.nombre;

export default function RecipeDetailV2({ preview = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authToken, userRole } = useAuth();
  const [recipe, setRecipe] = useState(preview ? previewRecipe : null);
  const [baseServings, setBaseServings] = useState(preview ? previewRecipe.servings : 1);
  const [servings, setServings] = useState(preview ? previewRecipe.servings : 1);
  const [ingredients, setIngredients] = useState(preview ? previewIngredients : {});
  const [subRecipes, setSubRecipes] = useState([]);
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(preview ? previewRecipe.userRating : 0);
  const [activeSection, setActiveSection] = useState('ingredients');
  const [menuOpen, setMenuOpen] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (preview) return;
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/recipes/${id}`, { headers });
        if (!response.ok) throw new Error('No hemos podido cargar esta receta');
        const data = await response.json();
        const initialServings = Math.max(1, number(data.servings) || 1);
        setRecipe(data);
        setBaseServings(initialServings);
        setServings(initialServings);
        setRating(number(data.userRating));

        const flattenedResponse = await fetch(`${import.meta.env.VITE_API_URL}/recipes/${id}/flatten?servings=${initialServings}`, { headers });
        if (!flattenedResponse.ok) throw new Error('No hemos podido calcular los ingredientes');
        const flattened = (await flattenedResponse.json()).flattened || {};
        const ingredientIds = Object.keys(flattened);
        const ingredientResponse = ingredientIds.length
          ? await fetch(`${import.meta.env.VITE_API_URL}/ingredients/by-ids?ids=${encodeURIComponent(ingredientIds.join(','))}`, { headers })
          : null;
        const ingredientDetails = ingredientResponse?.ok ? await ingredientResponse.json() : [];
        const detailsById = Object.fromEntries(ingredientDetails.map((item) => [String(item._id), item]));
        setIngredients(Object.fromEntries(ingredientIds.map((ingredientId) => [ingredientId, { ...detailsById[ingredientId], grams: number(flattened[ingredientId]) }])));

        const subComponents = (data.components || []).filter((component) => component.refModel === 'Recipe' && component.refId);
        if (subComponents.length) {
          const ids = subComponents.map((component) => String(component.refId));
          const subResponse = await fetch(`${import.meta.env.VITE_API_URL}/recipes/by-ids?ids=${encodeURIComponent(ids.join(','))}`, { headers });
          const subData = subResponse.ok ? await subResponse.json() : [];
          const subById = Object.fromEntries(subData.map((item) => [String(item._id), item]));
          setSubRecipes(subComponents.map((component) => ({ ...component, name: subById[String(component.refId)]?.name || 'Subreceta' })));
        }
      } catch (loadError) {
        setError(loadError.message || 'No hemos podido cargar esta receta');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authToken, id, preview]);

  const multiplier = servings / Math.max(1, baseServings);
  const ingredientEntries = useMemo(() => Object.entries(ingredients), [ingredients]);
  const prepMinutes = number(recipe?.prepTime?.hours) * 60 + number(recipe?.prepTime?.minutes);
  const macros = recipe?.totalMacronutrients || {};
  const macroTotal = number(macros.protein) + number(macros.carbs) + number(macros.fat);
  const moments = Array.isArray(recipe?.momentoDelDia) ? recipe.momentoDelDia : recipe?.momentoDelDia ? [recipe.momentoDelDia] : [];
  const cuisine = nameFor(recipe?.tipoDeCocina?.[0]);
  const difficulty = recipe?.difficulty === 'Baja' ? 'Fácil' : recipe?.difficulty === 'Alta' ? 'Difícil' : recipe?.difficulty || 'Media';

  const submitRating = async (_, value) => {
    if (!value || preview) {
      setRating(value || 0);
      return;
    }
    const previous = rating;
    setRating(value);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/recipes/${id}/rate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      if (!response.ok) throw new Error('No se pudo guardar tu valoración');
      const result = await response.json();
      setRecipe((current) => ({ ...current, rating: result.newAverage, ratingCount: result.newCount }));
      setRating(result.userRating);
      setNotice({ open: true, message: 'Valoración guardada', severity: 'success' });
    } catch (ratingError) {
      setRating(previous);
      setNotice({ open: true, message: ratingError.message, severity: 'error' });
    }
  };

  if (loading) return <div className="recipe-detail2-loading"><CircularProgress /><span>Preparando la receta…</span></div>;
  if (error || !recipe) return <div className="recipe-detail2-error"><MenuBookOutlined /><h1>No encontramos esta receta</h1><p>{error}</p><Link to="/recetas">Volver al recetario</Link></div>;

  return <ExperimentalPopupProvider>
    <main className="recipe-detail2-page">
      <nav className="recipe-detail2-topbar">
        <button type="button" onClick={() => navigate(-1)}><ArrowBack /> Volver</button>
        <div>
          {userRole === 'admin' && <button type="button" onClick={() => navigate(`/editar-receta/${recipe._id}`)}><EditOutlined /> Editar</button>}
        </div>
      </nav>

      <header className="recipe-detail2-hero">
        <div className="recipe-detail2-image">
          {recipe.mainImage ? <img src={getImage(recipe.mainImage)} alt={recipe.name} /> : <div><Restaurant /></div>}
          <div className="recipe-detail2-image-badges">
            {recipe.revisado && <span className="is-reviewed"><CheckCircle /> Receta revisada</span>}
            {number(recipe.userUsageCount) > 0 && <span>{recipe.userUsageCount} veces en tu menú</span>}
          </div>
        </div>
        <div className="recipe-detail2-intro">
          <div className="recipe-detail2-tags">{[...moments, ...(recipe.tipoPlato || [])].slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}</div>
          <h1>{recipe.name}</h1>
          <div className="recipe-detail2-rating-summary"><Star /><strong>{number(recipe.rating) ? number(recipe.rating).toFixed(1) : 'Nueva'}</strong>{number(recipe.ratingCount) > 0 && <span>{recipe.ratingCount} valoraciones</span>}</div>
          <div className="recipe-detail2-meta">
            {prepMinutes > 0 && <div><Schedule /><span><strong>{prepMinutes} min</strong><small>Tiempo total</small></span></div>}
            <div><Equalizer /><span><strong>{difficulty}</strong><small>Dificultad</small></span></div>
            {cuisine && <div><Restaurant /><span><strong>{cuisine}</strong><small>Tipo de cocina</small></span></div>}
          </div>
          <div className="recipe-detail2-actions">
            <button type="button" className="is-primary" onClick={() => setMenuOpen(true)}><Add /> Añadir al plan semanal</button>
            {recipe.externalUrl && <a href={recipe.externalUrl} target="_blank" rel="noreferrer"><OpenInNew /> Fuente original</a>}
          </div>
          {(authToken || preview) && <div className="recipe-detail2-your-rating">
            <div className="recipe-detail2-rating-copy"><strong>¿La has probado?</strong><small>Tu valoración ayuda a ordenar tu recetario.</small></div>
            <div className="recipe-detail2-rating-control">
              <Rating aria-label="Valorar receta" value={rating} onChange={submitRating} />
              <small>{rating ? `Tu nota: ${rating}/5` : 'Pulsa para valorar'}</small>
            </div>
          </div>}
        </div>
      </header>

      <div className="recipe-detail2-layout">
        <div className="recipe-detail2-main">
          <div className="recipe-detail2-content-toggle" role="tablist" aria-label="Contenido de la receta">
            <button type="button" role="tab" aria-selected={activeSection === 'ingredients'} aria-controls="recipe-detail2-content" className={activeSection === 'ingredients' ? 'is-active' : ''} onClick={() => setActiveSection('ingredients')}><Restaurant /><span><strong>Ingredientes</strong><small>{ingredientEntries.length} elementos</small></span></button>
            <button type="button" role="tab" aria-selected={activeSection === 'steps'} aria-controls="recipe-detail2-content" className={activeSection === 'steps' ? 'is-active' : ''} onClick={() => setActiveSection('steps')}><MenuBookOutlined /><span><strong>Preparación</strong><small>{recipe.steps?.length || 0} pasos</small></span></button>
          </div>

          <section id="recipe-detail2-content" className={`recipe-detail2-card ${activeSection === 'ingredients' ? 'recipe-detail2-ingredients' : 'recipe-detail2-steps'}`}>
            {activeSection === 'ingredients' ? <>
              <div className="recipe-detail2-section-title"><div><span>INGREDIENTES</span><h2>Todo lo que necesitas</h2></div><small>{checked.length}/{ingredientEntries.length} preparados</small></div>
              <div className="recipe-detail2-progress"><span style={{ width: `${ingredientEntries.length ? checked.length / ingredientEntries.length * 100 : 0}%` }} /></div>
              <div className="recipe-detail2-ingredient-list">
                {ingredientEntries.length ? ingredientEntries.map(([ingredientId, ingredient]) => {
                  const selected = checked.includes(ingredientId);
                  return <button type="button" key={ingredientId} className={selected ? 'is-checked' : ''} onClick={() => setChecked((current) => selected ? current.filter((value) => value !== ingredientId) : [...current, ingredientId])}>
                    <i>{selected && <Check />}</i><span>{ingredient.name || 'Ingrediente'}</span><strong>{labelFor(number(ingredient.grams) * multiplier, ingredient)}</strong>
                  </button>;
                }) : <p className="recipe-detail2-empty">No hay ingredientes disponibles.</p>}
              </div>
            </> : <>
              <div className="recipe-detail2-section-title"><div><span>PREPARACIÓN</span><h2>Cocina sin perderte</h2></div><small>{recipe.steps?.length || 0} pasos</small></div>
              {subRecipes.length > 0 && <div className="recipe-detail2-subrecipes"><strong>Prepara antes</strong>{subRecipes.map((subRecipe) => <Link key={String(subRecipe.refId)} to={`/recetas/${subRecipe.refId}`}><Restaurant /><span>{subRecipe.name}<small>Ver subreceta</small></span><LinkIcon /></Link>)}</div>}
              <ol>{(recipe.steps || []).map((step, index) => <li key={`${index}-${step.slice(0, 16)}`}><b>{index + 1}</b><p>{step}</p></li>)}</ol>
              {!recipe.steps?.length && !subRecipes.length && <p className="recipe-detail2-empty">Esta receta todavía no tiene pasos detallados.</p>}
            </>}
          </section>
        </div>

        <aside className="recipe-detail2-aside">
          <section className="recipe-detail2-card recipe-detail2-servings">
            <div><span>RACIONES</span><strong>Ajusta las cantidades</strong></div>
            <div className="recipe-detail2-counter"><button type="button" aria-label="Restar una ración" disabled={servings <= 1} onClick={() => setServings((value) => Math.max(1, value - 1))}><Remove /></button><span><strong>{servings}</strong><small>personas</small></span><button type="button" aria-label="Añadir una ración" onClick={() => setServings((value) => value + 1)}><Add /></button></div>
          </section>

          <section className="recipe-detail2-card recipe-detail2-nutrition">
            <div className="recipe-detail2-nutrition-head"><div><LocalFireDepartment /><span><small>POR RACIÓN</small><strong>{Math.round(number(macros.kilocalories))} kcal</strong></span></div></div>
            <div className="recipe-detail2-macros">
              {[['protein', 'Proteína', 'is-protein'], ['carbs', 'Carbohidratos', 'is-carbs'], ['fat', 'Grasas', 'is-fat']].map(([key, label, tone]) => {
                const amount = Math.round(number(macros[key]));
                const percent = macroTotal ? Math.round(amount / macroTotal * 100) : 0;
                return <div key={key} className={tone}><span><strong>{label}</strong><b>{amount} g</b></span><i><em style={{ width: `${percent}%` }} /></i><small>{percent}% de los macros</small></div>;
              })}
            </div>
          </section>

          {(recipe.restricciones || []).length > 0 && <section className="recipe-detail2-card recipe-detail2-suitable"><span>ADECUADA PARA</span><div>{recipe.restricciones.map((item) => <b key={nameFor(item)}>{nameFor(item)}</b>)}</div></section>}
        </aside>
      </div>

      <div className="recipe-detail2-mobile-cta"><button type="button" onClick={() => setMenuOpen(true)}><Add /> Añadir al plan</button></div>
      <AddToMenuPopup open={menuOpen} onClose={() => setMenuOpen(false)} recipeId={recipe._id} recipeName={recipe.name} authToken={authToken} />
      <Snackbar open={notice.open} autoHideDuration={3500} onClose={() => setNotice((current) => ({ ...current, open: false }))}><Alert severity={notice.severity}>{notice.message}</Alert></Snackbar>
    </main>
  </ExperimentalPopupProvider>;
}
